import React, { useCallback, useEffect, useMemo, useState } from "react";
import ThemeAwareSpinner from "../../../customComponents/ThemeAwareSpinner";
import "../FundingAccountPage/fundingAccount.css";
import {
    buildDefaultFuturesCloseInputs,
    decNum,
    resolveIncrementSize,
    sanitizeIncrementInput,
    snapAndCapCloseQty,
    snapToIncrementInput,
} from "./futuresIncrementHelpers";

const PCT_OPTIONS = [25, 50, 75, 100];

function formatQtyDisplay(n, stepSize) {
    if (!Number.isFinite(n) || n <= 0) return "—";
    const snapped = snapAndCapCloseQty(String(n), stepSize, n);
    return snapped || String(n);
}

function formatPairLabel(pos, contract) {
    const base = pos?.base_currency || contract?.base_currency || "";
    const quote = pos?.quote_currency || contract?.quote_currency || contract?.margin_asset || "USDT";
    if (base && quote) return `${base}/${quote}`;
    const sym = String(pos?.symbol || "");
    if (sym.includes("/")) return sym;
    if (quote && sym.toUpperCase().endsWith(String(quote).toUpperCase())) {
        return `${sym.slice(0, -quote.length)}/${quote}`;
    }
    return sym || "—";
}

function formatMarginMode(mode) {
    const m = String(mode || "").toUpperCase();
    if (m === "ISOLATED") return "Isolated";
    if (m === "CROSS") return "Cross";
    return mode || "—";
}

/**
 * Futures close position — Market / Limit modal (table no longer has inline inputs).
 */
export default function FuturesClosePositionModal({
    pos,
    contract,
    loading = false,
    fieldError = null,
    onClose,
    onConfirm,
}) {
    const defaults = useMemo(
        () => buildDefaultFuturesCloseInputs(pos, contract),
        [pos, contract]
    );
    const tickSize = defaults.tickSize || resolveIncrementSize(null, contract, "tick_size", "0.01");
    const stepSize = defaults.stepSize || resolveIncrementSize(null, contract, "step_size", "0.001");
    const holding = decNum(pos?.quantity);
    const markPrice = decNum(pos?.mark_price);

    const [orderType, setOrderType] = useState("MARKET");
    const [pct, setPct] = useState(100);
    const [qty, setQty] = useState(defaults.qty || "");
    const [price, setPrice] = useState(defaults.price || "");
    const [localError, setLocalError] = useState(null);

    useEffect(() => {
        setOrderType("MARKET");
        setPct(100);
        setQty(defaults.qty || "");
        setPrice(defaults.price || "");
        setLocalError(null);
    }, [pos?._id, defaults.qty, defaults.price]);

    useEffect(() => {
        setLocalError(fieldError || null);
    }, [fieldError]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && !loading) onClose?.();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose, loading]);

    const baseCc = pos?.base_currency || contract?.base_currency || "BTC";
    const quoteCc = pos?.quote_currency || contract?.quote_currency || contract?.margin_asset || "USDT";
    const pairLabel = formatPairLabel(pos, contract);
    const marginModeLabel = formatMarginMode(pos?.margin_mode);
    const isLong = String(pos?.side || "").toUpperCase() === "LONG";
    const sideLabel = isLong ? "Long" : String(pos?.side || "").toUpperCase() === "SHORT" ? "Short" : "—";

    const applyPct = useCallback(
        (p) => {
            setPct(p);
            if (!Number.isFinite(holding) || holding <= 0) {
                setQty("");
                return;
            }
            const raw = holding * (p / 100);
            setQty(snapAndCapCloseQty(String(raw), stepSize, holding) || "");
        },
        [holding, stepSize]
    );

    const qtyNum = decNum(qty);
    const priceNum = orderType === "LIMIT" ? decNum(price) : (markPrice > 0 ? markPrice : decNum(price));
    const estValue =
        Number.isFinite(qtyNum) && qtyNum > 0 && Number.isFinite(priceNum) && priceNum > 0
            ? qtyNum * priceNum
            : null;

    const canSubmit =
        !loading &&
        Number.isFinite(qtyNum) &&
        qtyNum > 0 &&
        (orderType === "MARKET" || (Number.isFinite(decNum(price)) && decNum(price) > 0));

    const handleConfirm = () => {
        if (!canSubmit) return;
        const snappedQty = snapAndCapCloseQty(qty, stepSize, holding);
        const snappedPrice =
            orderType === "LIMIT" ? snapToIncrementInput(price, tickSize) : undefined;
        onConfirm?.({
            orderType,
            quantity: snappedQty,
            price: snappedPrice,
        });
    };

    if (!pos) return null;

    return (
        <>
            <div
                className="modal-backdrop fade show"
                onClick={() => !loading && onClose?.()}
                aria-hidden
            />
            <div
                className="modal fade kyc_modal margin-modal-container show d-block"
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
                style={{ zIndex: 1055 }}
            >
                <div
                    className="modal-dialog modal-dialog-centered close-position-modal-dialog agce-ft-close-modal-dialog"
                    role="document"
                >
                    <div className="modal-content close-position-modal-content agce-ft-close-modal-content">
                        <div className="modal-header border-0 pb-1 px-4 pt-4">
                            <div>
                                <h5 className="modal-title fw-bold close-position-modal-title mb-1">
                                    Close position
                                </h5>
                                <div className="d-flex align-items-center gap-2 flex-wrap close-position-modal-subtitle">
                                    <span className="fw-semibold text-body">{pairLabel}</span>
                                    <span aria-hidden>·</span>
                                    <span>{marginModeLabel}</span>
                                    <span
                                        className={`agce-ft-close-modal-side-pill ${
                                            isLong
                                                ? "agce-ft-close-modal-side-pill--long"
                                                : "agce-ft-close-modal-side-pill--short"
                                        }`}
                                    >
                                        {sideLabel}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn-close margin-modal-close"
                                aria-label="Close"
                                disabled={loading}
                                onClick={onClose}
                            />
                        </div>

                        <div className="modal-body px-4 pt-3 pb-0 modern-margin-form">
                            <div className="agce-ft-close-modal-tabs" role="tablist">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={orderType === "MARKET"}
                                    className={`agce-ft-close-modal-tab${
                                        orderType === "MARKET" ? " is-active" : ""
                                    }`}
                                    onClick={() => setOrderType("MARKET")}
                                    disabled={loading}
                                >
                                    Market
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={orderType === "LIMIT"}
                                    className={`agce-ft-close-modal-tab${
                                        orderType === "LIMIT" ? " is-active" : ""
                                    }`}
                                    onClick={() => setOrderType("LIMIT")}
                                    disabled={loading}
                                >
                                    Limit
                                </button>
                            </div>

                            {orderType === "LIMIT" ? (
                                <div className="crypto_selectcoin crypto_selectcoin-2 mb-3">
                                    <div className="agce-ft-close-modal-field-label-row">
                                        <h6 className="mb-0">Limit price</h6>
                                        {markPrice > 0 ? (
                                            <span className="agce-ft-close-modal-mark-hint">
                                                Mark:{" "}
                                                {markPrice.toLocaleString("en-US", {
                                                    maximumFractionDigits: 8,
                                                })}
                                            </span>
                                        ) : null}
                                    </div>
                                    <div className={`price_max_total${localError?.field === "price" ? " is-error" : ""}`}>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            autoComplete="off"
                                            placeholder="Enter limit price"
                                            value={price}
                                            disabled={loading}
                                            onChange={(e) => {
                                                setLocalError((prev) => (prev?.field === "price" ? null : prev));
                                                setPrice(sanitizeIncrementInput(e.target.value, tickSize));
                                            }}
                                            onBlur={() =>
                                                setPrice(snapToIncrementInput(price, tickSize))
                                            }
                                            aria-label="Limit price"
                                        />
                                        <div className="d-flex gap-0 align-items-center">{quoteCc}</div>
                                    </div>
                                    {localError?.field === "price" ? <div className="trade_field_error">{localError.text}</div> : null}
                                </div>
                            ) : null}

                            <label className="repay-label">Amount to close</label>
                            <div className="agce-ft-close-modal-pcts">
                                {PCT_OPTIONS.map((p) => (
                                    <button
                                        key={p}
                                        type="button"
                                        className={`agce-ft-close-modal-pct${pct === p ? " is-active" : ""}`}
                                        disabled={loading}
                                        onClick={() => applyPct(p)}
                                    >
                                        {p}%
                                    </button>
                                ))}
                            </div>
                            <div className="crypto_selectcoin crypto_selectcoin-2 mb-3">
                                <div className={`price_max_total${localError?.field === "amount" ? " is-error" : ""}`}>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        autoComplete="off"
                                        placeholder="Enter amount to close"
                                        value={qty}
                                        disabled={loading}
                                        onChange={(e) => {
                                            setPct(null);
                                            setLocalError((prev) => (prev?.field === "amount" ? null : prev));
                                            setQty(
                                                sanitizeIncrementInput(e.target.value, stepSize)
                                            );
                                        }}
                                        onBlur={() =>
                                            setQty(snapAndCapCloseQty(qty, stepSize, holding))
                                        }
                                        aria-label="Amount to close"
                                    />
                                    <div className="d-flex gap-0 align-items-center">
                                        {baseCc}
                                        <button
                                            type="button"
                                            disabled={loading}
                                            onClick={() => applyPct(100)}
                                        >
                                            Max
                                        </button>
                                    </div>
                                </div>
                                {localError?.field === "amount" ? <div className="trade_field_error">{localError.text}</div> : null}
                            </div>

                            <div className="margin-info-rows mb-2">
                                <div className="margin-info-row">
                                    <span>Holding</span>
                                    <span>
                                        {formatQtyDisplay(holding, stepSize)} {baseCc}
                                    </span>
                                </div>
                                <div className="margin-info-row">
                                    <span>Est. value</span>
                                    <span>
                                        {estValue != null
                                            ? `${estValue.toLocaleString("en-US", {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                              })} ${quoteCc}`
                                            : "—"}
                                    </span>
                                </div>
                            </div>

                            <div className="margin-modal-note mb-3">
                                <i className="ri-information-fill" />
                                <span>
                                    {orderType === "MARKET"
                                        ? "Fills at the current market price."
                                        : "Order rests on the book until filled at your price."}
                                </span>
                            </div>
                        </div>

                        <div className="modal-footer border-0 px-4 pb-4 pt-1">
                            <button
                                type="button"
                                className={`btn w-100 close-position-confirm-btn agce-ft-close-modal-confirm ${
                                    isLong
                                        ? "agce-ft-close-modal-confirm--long"
                                        : "agce-ft-close-modal-confirm--short"
                                }`}
                                onClick={handleConfirm}
                                disabled={!canSubmit}
                            >
                                {loading ? (
                                    <>
                                        <ThemeAwareSpinner as="span" size="sm" className="me-2" aria-hidden="true" />
                                        Closing…
                                    </>
                                ) : (
                                    `Close ${sideLabel.toLowerCase()}`
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
